// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

import { supabase } from "./supabase";
import type { StorageProvider } from "./persistence";
import type { ProjectState, UserPreferences, ProjectShare } from "../types";

const DEFAULT_SHORTCUTS = { enterAddRow: true, ctrlZUndo: true, questionShortcuts: true };

export class SupabaseStorageProvider implements StorageProvider {
  private userId: string;

  constructor(userId: string) {
    this.userId = userId;
  }

  // ── Audit Logging ──
  // Routes through a SECURITY DEFINER RPC (migration 017) so the server
  // validates the action enum and resource ownership before writing.
  // Fire-and-forget: never throws, never blocks the caller.
  private auditLog(
    action: string,
    resourceType: "project" | "snapshot" | "share" | "account",
    resourceId?: string,
  ): void {
    if (!supabase) return;
    supabase
      .rpc("record_audit_event", {
        p_action: action,
        p_resource_type: resourceType,
        p_resource_id: resourceId ?? null,
      })
      .then(({ error }) => {
        // Silently swallow — audit failures must never surface to the user
        if (error) console.warn("[audit]", action, error.message);
      });
  }

  // ── Current Project ──
  // In Supabase mode, "current project" is managed by App state (which project is open).
  // These methods are kept for interface compatibility but delegate to the project-specific methods.
  loadCurrentProject(): ProjectState | null {
    return null; // App will call loadProjectById instead
  }

  saveCurrentProject(_state: ProjectState): void {
    // No-op — use saveProjectById
  }

  // ── Project CRUD ──
  listProjects(): Record<string, ProjectState & { savedAt: string }> {
    // Synchronous for interface compat — real usage goes through async methods below
    return {};
  }

  saveProject(_name: string, _state: ProjectState): void {
    // No-op — use async methods below
  }

  deleteProject(_name: string): void {
    // No-op — use async methods below
  }

  // ── Async Project Methods (the real Supabase operations) ──
  async listProjectsAsync(): Promise<Array<{
    id: string; name: string; address: string; total: number; costPerSF: number; savedAt: string;
    snapshotCount: number; ownerId: string; ownerName: string; role: string;
    orgId: string | null; orgName: string | null; sharedVia: "owner" | "org" | "share";
    isActive: boolean; photoPath: string | null;
  }>> {
    if (!supabase) return [];

    // photo_path is fetched separately and allowed to fail.
    //
    // It was part of this SELECT, and when the column did not yet exist
    // Postgres rejected the whole query for that one unknown name -- so a
    // cosmetic cover photo made every project vanish from the list. A
    // decoration must never be able to hide someone's work. Anything the list
    // cannot live without belongs in the query below; anything else does not.
    const { data, error } = await supabase
      .from("projects")
      .select(`
        id, name, address, building_area, overhead, profit, contingency,
        divisions, item_state, created_at, updated_at, owner_id, org_id, is_active,
        profiles!projects_owner_id_fkey ( display_name ),
        organizations ( name ),
        snapshots ( id )
      `)
      .is("deleted_at", null)
      .order("updated_at", { ascending: false });

    if (error || !data) { console.error("listProjects error:", error); return []; }

    let photoByProject: Record<string, string> = {};
    try {
      const { data: photos } = await supabase
        .from("projects").select("id, photo_path").is("deleted_at", null);
      if (photos) {
        photoByProject = Object.fromEntries(
          photos.filter((r: any) => r.photo_path).map((r: any) => [r.id, r.photo_path]),
        );
      }
    } catch { /* no cover photos; the list still renders */ }

    return data.map((p: any) => {
      const summary = this.computeSummary(p.divisions, p.item_state, p.overhead, p.profit, p.contingency, p.building_area);
      const isOwner = p.owner_id === this.userId;
      return {
        id: p.id,
        name: p.name,
        address: p.address || "",
        total: summary.total,
        costPerSF: summary.costPerSF,
        savedAt: p.updated_at,
        snapshotCount: p.snapshots?.length || 0,
        ownerId: p.owner_id,
        ownerName: p.profiles?.display_name || "",
        orgId: p.org_id || null,
        orgName: p.organizations?.name || null,
        role: isOwner ? "owner" : "editor",
        sharedVia: isOwner ? "owner" : p.org_id ? "org" : "share",
        isActive: p.is_active !== false,
        photoPath: photoByProject[p.id] || null,
      };
    });
  }

  // ── Project cover photos ────────────────────────────────────────────
  // The bucket is private and served through short-lived signed URLs, like
  // `proposals`. A site photo can show a client's house; a public bucket
  // would make that a permanent guessable URL.
  private static readonly PHOTO_BUCKET = "project-photos";

  /** Upload a prepared (cropped, downscaled) cover image. Returns its path. */
  async uploadProjectPhoto(projectId: string, blob: Blob): Promise<string> {
    if (!supabase) throw new Error("Not connected");
    await supabase.storage
      .createBucket(SupabaseStorageProvider.PHOTO_BUCKET, { public: false })
      .catch(() => {});
    // Timestamped rather than fixed, so a replacement never serves a stale
    // cached image under the same URL.
    const path = `${projectId}/${Date.now()}.jpg`;
    const { error } = await supabase.storage
      .from(SupabaseStorageProvider.PHOTO_BUCKET)
      .upload(path, blob, { upsert: true, contentType: "image/jpeg" });
    if (error) {
      console.error("[cover photo] upload failed:", error);
      // createBucket above is service-role only on most projects, so it
      // quietly does nothing and the upload is the first thing to notice.
      if (/bucket/i.test(error.message)) {
        throw new Error(
          `The "${SupabaseStorageProvider.PHOTO_BUCKET}" storage bucket does not exist yet. ` +
          `Create it in Supabase → Storage as a PRIVATE bucket, then try again.`,
        );
      }
      throw new Error(`Upload failed: ${error.message}`);
    }
    return path;
  }

  /** Point a project at a cover photo, or clear it. */
  async setProjectPhoto(projectId: string, photoPath: string | null): Promise<void> {
    if (!supabase) return;
    const { error } = await supabase.from("projects")
      .update({ photo_path: photoPath }).eq("id", projectId);
    if (error) {
      console.error("[cover photo] could not set photo_path:", error);
      if (/photo_path|column/i.test(error.message)) {
        throw new Error(
          "The projects table has no photo_path column yet. Run: " +
          "ALTER TABLE projects ADD COLUMN IF NOT EXISTS photo_path text;",
        );
      }
      throw new Error(error.message);
    }
  }

  /** Remove the stored file. Best effort: a stray object is harmless. */
  async deleteProjectPhoto(photoPath: string): Promise<void> {
    if (!supabase) return;
    await supabase.storage.from(SupabaseStorageProvider.PHOTO_BUCKET)
      .remove([photoPath]).catch(() => {});
  }

  /**
   * Sign every path in ONE call rather than one per card -- the home screen
   * asks for all of them at once.
   */
  async signProjectPhotos(paths: string[]): Promise<Record<string, string>> {
    if (!supabase || paths.length === 0) return {};
    const unique = Array.from(new Set(paths));
    const { data, error } = await supabase.storage
      .from(SupabaseStorageProvider.PHOTO_BUCKET)
      .createSignedUrls(unique, 3600);
    if (error || !data) return {};
    const out: Record<string, string> = {};
    data.forEach((d: any) => { if (d?.path && d?.signedUrl) out[d.path] = d.signedUrl; });
    return out;
  }

  async loadProjectById(projectId: string): Promise<ProjectState | null> {
    if (!supabase) return null;

    const { data, error } = await supabase
      .from("projects")
      .select("*")
      .eq("id", projectId)
      .single();

    if (error || !data) { console.error("loadProject error:", error); return null; }

    // Map DB columns to ProjectState. extra_state holds everything without a
    // dedicated column (bids, buyout, RFIs, leveling, drawing list, …) — see
    // migration 025. Spread it last; its keys are disjoint from the columns.
    const extra = (data.extra_state as Record<string, any>) || {};
    return {
      projectName: data.name,
      address: data.address || "",
      buildingArea: data.building_area || 0,
      duration: data.duration || 12,
      overhead: data.overhead || 10,
      profit: data.profit || 8,
      contingency: data.contingency || 5,
      proposals: data.proposals || [],
      proposalAreas: data.proposal_areas || {},
      divisionSettings: data.division_settings || [],
      subSettings: data.sub_settings || {},
      divisions: data.divisions || [],
      itemState: data.item_state || {},
      isActive: data.is_active !== false,
      ownerId: data.owner_id || null,
      ...extra,
    };
  }

  // Keys persisted in projects.extra_state (jsonb) — everything the estimate
  // saves that has no dedicated column. Anything listed here round-trips via
  // load/save and is merge-patched atomically so a partial save (e.g. an
  // openItems-only patch) never clobbers the other keys.
  private static readonly EXTRA_STATE_KEYS = [
    "markupMode", "divisionMarkups", "tradeSources",
    "bidScopes", "bidPartners", "bids", "levelingAdjustments", "bidAlignments",
    "costSFCents", "rfis", "buyoutItems", "projectExclusions",
    "drawingList", "projectNotes", "openItems", "jurisdictionInfo",
    "schedule", "costSchedule", "submittals", "siteLogistics", "historyNotes",
    "migratedOpenItems",
  ] as const;

  async saveProjectById(projectId: string, state: Partial<ProjectState>): Promise<void> {
    if (!supabase) return;

    const updates: Record<string, any> = {};
    if (state.projectName !== undefined) updates.name = state.projectName;
    if (state.address !== undefined) updates.address = state.address;
    if (state.buildingArea !== undefined) updates.building_area = state.buildingArea;
    if (state.duration !== undefined) updates.duration = state.duration;
    if (state.overhead !== undefined) updates.overhead = state.overhead;
    if (state.profit !== undefined) updates.profit = state.profit;
    if (state.contingency !== undefined) updates.contingency = state.contingency;
    if (state.proposals !== undefined) updates.proposals = state.proposals;
    if (state.proposalAreas !== undefined) updates.proposal_areas = state.proposalAreas;
    if (state.divisionSettings !== undefined) updates.division_settings = state.divisionSettings;
    if (state.subSettings !== undefined) updates.sub_settings = state.subSettings;
    if (state.divisions !== undefined) updates.divisions = state.divisions;
    if (state.itemState !== undefined) updates.item_state = state.itemState;
    if (state.isActive !== undefined) updates.is_active = state.isActive;

    // Collect any extra_state keys present in this (possibly partial) save.
    const patch: Record<string, any> = {};
    for (const k of SupabaseStorageProvider.EXTRA_STATE_KEYS) {
      if ((state as any)[k] !== undefined) patch[k] = (state as any)[k];
    }

    if (Object.keys(updates).length > 0) {
      const { error } = await supabase.from("projects").update(updates).eq("id", projectId);
      if (error) { console.error("saveProject error:", error); return; }
    }
    if (Object.keys(patch).length > 0) {
      // Atomic top-level merge — preserves keys not in this patch.
      const { error } = await supabase.rpc("patch_project_extra_state", {
        p_project_id: projectId,
        p_patch: patch,
      });
      if (error) { console.error("saveProject extra_state error:", error); return; }
    }
    this.auditLog("project.save", "project", projectId);
  }

  async createProject(state: ProjectState, orgId?: string): Promise<string | null> {
    if (!supabase) return null;

    // Capture any extra_state keys present on creation (e.g. AI imports that
    // arrive with bids/buyout already populated) so they aren't lost.
    const extra: Record<string, any> = {};
    for (const k of SupabaseStorageProvider.EXTRA_STATE_KEYS) {
      if ((state as any)[k] !== undefined) extra[k] = (state as any)[k];
    }

    const { data, error } = await supabase
      .from("projects")
      .insert({
        owner_id: this.userId,
        org_id: orgId || null,
        name: state.projectName,
        address: state.address || "",
        building_area: state.buildingArea,
        duration: state.duration,
        overhead: state.overhead,
        profit: state.profit,
        contingency: state.contingency,
        proposals: state.proposals,
        proposal_areas: state.proposalAreas,
        division_settings: state.divisionSettings,
        sub_settings: state.subSettings,
        divisions: state.divisions,
        item_state: state.itemState,
        is_active: state.isActive !== false,
        extra_state: extra,
      })
      .select("id")
      .single();

    if (error) { console.error("createProject error:", error); return null; }
    const newId = data?.id || null;
    if (newId) this.auditLog("project.create", "project", newId);
    return newId;
  }

  async deleteProjectById(projectId: string): Promise<{ error?: string }> {
    if (!supabase) return { error: "Not connected" };
    const { error } = await supabase
      .from("projects")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", projectId);
    if (error) { console.error("deleteProject error:", error); return { error: error.message }; }
    this.auditLog("project.delete", "project", projectId);
    return {};
  }

  async listDeletedProjects(): Promise<Array<{ id: string; name: string; address: string; deletedAt: string }>> {
    if (!supabase) return [];
    const { data, error } = await supabase
      .from("projects")
      .select("id, name, address, deleted_at")
      .not("deleted_at", "is", null)
      .order("deleted_at", { ascending: false });
    if (error || !data) return [];
    return data.map((p: any) => ({ id: p.id, name: p.name, address: p.address || "", deletedAt: p.deleted_at }));
  }

  async restoreProject(projectId: string): Promise<void> {
    if (!supabase) return;
    const { error } = await supabase.from("projects").update({ deleted_at: null }).eq("id", projectId);
    if (error) console.error("restoreProject error:", error);
  }

  async permanentlyDeleteProject(projectId: string): Promise<void> {
    if (!supabase) return;
    const { error } = await supabase.from("projects").delete().eq("id", projectId);
    if (error) console.error("permanentlyDeleteProject error:", error);
  }

  // ── Snapshots ──
  async listSnapshots(projectId: string) {
    if (!supabase) return [];
    // Try with deleted_at filter (requires migration 010)
    const { data, error } = await supabase
      .from("snapshots")
      .select("id, label, created_at, locked, state, created_by, deleted_at, profiles!snapshots_created_by_fkey(display_name)")
      .eq("project_id", projectId)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });
    if (error) {
      // Migration 010 not run yet — fall back to query without deleted_at
      console.warn("listSnapshots: deleted_at column missing, falling back:", error.message);
      const { data: d2, error: e2 } = await supabase
        .from("snapshots")
        .select("id, label, created_at, locked, state, created_by, profiles!snapshots_created_by_fkey(display_name)")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false });
      if (e2) { console.error("listSnapshots fallback error:", e2); return []; }
      return d2 || [];
    }
    return data || [];
  }

  async listDeletedSnapshots(projectId: string) {
    if (!supabase) return [];
    const { data, error } = await supabase
      .from("snapshots")
      .select("id, label, created_at, locked, state, created_by, deleted_at, profiles!snapshots_created_by_fkey(display_name)")
      .eq("project_id", projectId)
      .not("deleted_at", "is", null)
      .order("deleted_at", { ascending: false });
    if (error) {
      // Migration 010 not run yet — no deleted snapshots to show
      console.warn("listDeletedSnapshots: deleted_at column missing:", error.message);
      return [];
    }
    return data || [];
  }

  async saveSnapshot(projectId: string, label: string, state: ProjectState) {
    if (!supabase) return;
    const { data: snap, error } = await supabase.from("snapshots").insert({
      project_id: projectId,
      label,
      created_by: this.userId,
      locked: true,
      state,
    }).select("id").single();
    if (error) { console.error("saveSnapshot error:", error); return; }
    this.auditLog("snapshot.create", "snapshot", snap?.id);
  }

  async softDeleteSnapshotById(snapshotId: string): Promise<{ ok: boolean; migrationMissing: boolean }> {
    if (!supabase) return { ok: false, migrationMissing: false };
    const { data, error } = await supabase
      .from("snapshots")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", snapshotId)
      .select("id");
    if (error) {
      const migrationMissing = error.message?.includes("deleted_at") || error.code === "42703";
      console.warn("softDeleteSnapshot error:", error.message);
      return { ok: false, migrationMissing };
    }
    // 0 rows updated = RLS blocked it (no UPDATE policy) or wrong ID
    if (!data || data.length === 0) {
      console.warn("softDeleteSnapshot: 0 rows updated — missing UPDATE RLS policy (run migration 011)");
      return { ok: false, migrationMissing: false };
    }
    this.auditLog("snapshot.delete", "snapshot", snapshotId);
    return { ok: true, migrationMissing: false };
  }

  async restoreSnapshotById(snapshotId: string) {
    if (!supabase) return;
    const { error } = await supabase
      .from("snapshots")
      .update({ deleted_at: null })
      .eq("id", snapshotId);
    if (error) console.error("restoreSnapshot error:", error);
  }

  async permanentlyDeleteSnapshotById(snapshotId: string) {
    if (!supabase) return;
    const { error } = await supabase.from("snapshots").delete().eq("id", snapshotId);
    if (error) console.error("permanentlyDeleteSnapshot error:", error);
  }

  async renameSnapshotById(snapshotId: string, newLabel: string) {
    if (!supabase) return;
    const { error } = await supabase.from("snapshots").update({ label: newLabel }).eq("id", snapshotId);
    if (error) console.error("renameSnapshot error:", error);
  }

  // ── Preferences ──
  loadPreferences(): UserPreferences {
    return { shortcuts: DEFAULT_SHORTCUTS, typologyPrefs: {}, columnVisibility: {} };
  }

  saveShortcuts(_shortcuts: UserPreferences["shortcuts"]): void {}
  saveTypologyPrefs(_prefs: UserPreferences["typologyPrefs"]): void {}

  loadHistoryNotes(): Record<string, string> { return {}; }
  saveHistoryNotes(_notes: Record<string, string>): void {}

  getCurrentUser() {
    return { id: this.userId, email: "" };
  }

  isAuthenticated() {
    return true;
  }

  // ── Sharing ──
  async listProjectShares(projectId: string): Promise<ProjectShare[]> {
    if (!supabase) return [];
    const { data, error } = await supabase
      .from("project_shares")
      .select("user_id, role, created_at, profiles ( email, display_name )")
      .eq("project_id", projectId)
      .order("created_at", { ascending: true });
    if (error || !data) { console.error("listProjectShares error:", error); return []; }
    return data.map((s: any) => ({
      userId: s.user_id,
      email: s.profiles?.email || "",
      displayName: s.profiles?.display_name || "",
      role: s.role,
      createdAt: s.created_at,
    }));
  }

  async shareProject(projectId: string, email: string, role: string): Promise<{ error?: string }> {
    if (!supabase) return { error: "Not connected" };
    // Look up user by email
    const { data: profile, error: lookupErr } = await supabase
      .from("profiles")
      .select("id")
      .eq("email", email.toLowerCase().trim())
      .maybeSingle();
    if (lookupErr) return { error: lookupErr.message };
    if (!profile) return { error: "No user found with that email. They must sign up first." };
    // Insert share
    const { error } = await supabase
      .from("project_shares")
      .insert({ project_id: projectId, user_id: profile.id, role, invited_by: this.userId });
    if (error) {
      if (error.code === "23505") return { error: "This user already has access." };
      return { error: error.message };
    }
    this.auditLog("share.create", "share", projectId);
    return {};
  }

  async unshareProject(projectId: string, userId: string): Promise<void> {
    if (!supabase) return;
    const { error } = await supabase
      .from("project_shares")
      .delete()
      .eq("project_id", projectId)
      .eq("user_id", userId);
    if (error) { console.error("unshareProject error:", error); return; }
    this.auditLog("share.revoke", "share", projectId);
  }

  async setProjectActive(projectId: string, isActive: boolean): Promise<void> {
    if (!supabase) return;
    const { error } = await supabase
      .from("projects")
      .update({ is_active: isActive })
      .eq("id", projectId);
    if (error) console.error("setProjectActive error:", error);
  }

  // ── GDPR / Privacy ──
  async exportMyData(): Promise<{ data?: unknown; error?: string }> {
    if (!supabase) return { error: "Not connected" };
    const { data, error } = await supabase.rpc("export_my_data");
    if (error) { console.error("exportMyData error:", error); return { error: error.message }; }
    this.auditLog("account.export", "account");
    return { data };
  }

  async deleteMyData(): Promise<{ error?: string }> {
    if (!supabase) return { error: "Not connected" };
    const { error } = await supabase.rpc("delete_my_data");
    if (error) { console.error("deleteMyData error:", error); return { error: error.message }; }
    // Note: caller must then call supabase.auth.signOut() — we don't do it here
    // so the caller can control navigation.
    return {};
  }

  async updateProjectOrg(projectId: string, orgId: string | null): Promise<void> {
    if (!supabase) return;
    const { error } = await supabase
      .from("projects")
      .update({ org_id: orgId })
      .eq("id", projectId);
    if (error) console.error("updateProjectOrg error:", error);
  }

  // ── Helpers ──
  private computeSummary(divisions: any[], itemState: any, overhead: number, profit: number, contingency: number, buildingArea: number) {
    let sub = 0;
    function* walkItems(rows: any[]): Generator<any> {
      for (const r of rows) {
        if (r.type === "group") yield* walkItems(r.children);
        else yield r;
      }
    }
    (divisions || []).forEach((div: any) =>
      (div.subcategories || []).forEach((s: any) =>
        [...walkItems(s.rows || [])].forEach((item: any) => {
          const li = (itemState || {})[item.id] || {};
          if (li.included && !li.altType) sub += (li.qty || 0) * (li.unitCost || 0);
        })
      )
    );
    const oh = sub * ((overhead || 0) / 100);
    const pr = sub * ((profit || 0) / 100);
    const bc = sub + oh + pr;
    const co = bc * ((contingency || 0) / 100);
    const total = bc + co;
    return { total, costPerSF: (buildingArea || 0) > 0 ? total / buildingArea : 0 };
  }
}
